import React, { memo, useContext, FC } from 'react';

import { ThemeContext } from 'styled-components';
import './avatar.css'


interface AvatarProps {
  /**
   * Cor a ser exibida do fundo do avatar
   * Caso não definida, será uma pré definida baseada na letra inicial da pessoa
   */
  cor?: string
  /**
   * Nome cujo qual serão obtidas as siglas
   */
  nome?: string
  /**
   * Iniciais a serem apresentadas no avatar.
   * Caso não seja definido, será obtida pelo nome
   */
  iniciais?: string
  loading?: boolean
}


/**
  * Baseado em: https://codepen.io/arturheinze/pen/ZGvOMw
  *
  * LetterAvatar
  * 
  * Artur Heinze
  * Create Letter avatar based on Initials
  * based on https://gist.github.com/leecrossley/6027780
  */
export const Avatar: FC<AvatarProps> = ({cor, nome, loading, iniciais, ...props}) => {
  const themeContext = useContext(ThemeContext);

  const nomeExibido = gerarIniciais(loading === true, nome, iniciais);
  
  const corPreferencial = loading || (!loading && (nomeExibido === undefined || nomeExibido === '')) ? themeContext.background.loading : undefined
  const src = generateDataURI(nomeExibido, corPreferencial);

  return (
    <img {...props} className="avatar-img" src={src} alt={nome} />
  )
}

function gerarIniciais(loading: boolean, nome?: string, iniciais?: string): string {
  if (loading) {
    return '';
  } else if (iniciais !== undefined) {
    return iniciais;
  } else if (nome !== undefined) {
    const partesNome = nome.split(' ');
    if (partesNome.length === 1) {
      return partesNome[0] ? partesNome[0].charAt(0) : '';
    } else {
      return partesNome[0].charAt(0) + partesNome[1].charAt(0);
    }
  } else {
    return ''
  }
}

function generateDataURI(initials: string, corPreferencial?: string, size?: number) {
  size = size || 60;

  if (window.devicePixelRatio) {
      size = (size * window.devicePixelRatio);
  }

  const colors = [
    "#1abc9c", "#2ecc71", "#3498db", "#9b59b6", "#34495e", "#16a085", "#27ae60", "#2980b9", "#8e44ad", "#2c3e50", 
    "#f1c40f", "#e67e22", "#e74c3c", "#8987f2", "#95a5a6", "#f39c12", "#d35400", "#c0392b", "#bdc3c7", "#7f8c8d"
  ]

  const colorIndex = (initials.charCodeAt(0)-64) % 20;
  const color = corPreferencial ?? colors[colorIndex - 1];
  
  const canvas = generateCanvas(initials, color, size);
  return canvas.toDataURL();
}


function generateCanvas(initials: string, color: string, figureSize: number) {
  const canvas = document.createElement('canvas');
  canvas.width = figureSize;
  canvas.height = figureSize;
  
  const context: any = canvas.getContext("2d");

  context.fillStyle = color;
  context.fillRect (0, 0, canvas.width, canvas.height);
  context.font = Math.round(canvas.width/2)+"px Arial";
  context.textAlign = "center";
  context.fillStyle = "#FFF";
  context.fillText(initials, figureSize / 2, figureSize / 1.5);

  return canvas;
}

export default memo<AvatarProps>(Avatar);
